import { Dispatch } from 'redux';

import { updateMaintenance } from '../ducks/platformData';
import { logout } from '../ducks/auth';
import { LogoutTypes, WSMessageTypes } from './constants';
import { setPresenceData } from '../ducks/presence';

let ws: WebSocket | undefined;
let identity: { id: string; token: string; dispatch: Dispatch; isAdmin: boolean } | undefined;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let intentionalClose = false;
let lastHeartbeat: { state: 'ACTIVE' | 'IDLE'; context?: string; url?: string } | undefined;

const MAX_BACKOFF_MS = 30000;
const BASE_BACKOFF_MS = 1000;

const scheduleReconnect = () => {
  if (intentionalClose || !identity) return;
  const delay = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** reconnectAttempts);
  const jitter = Math.floor(delay * 0.25 * Math.random());
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => open(), delay + jitter);
};

const open = () => {
  if (!identity) return;
  const { id, token, dispatch, isAdmin } = identity;
  let socket: WebSocket;
  try {
    socket = new WebSocket(`wss://${window.location.host}/ws`);
  } catch (e) {
    // Constructor can throw (SecurityError / malformed URL); keep retrying.
    console.error(e);
    scheduleReconnect();
    return;
  }
  ws = socket;
  // send/close close over `socket` (this specific instance), not the shared `ws`,
  // so a stale socket's late send/close can't act on the current connection.
  // (onmessage/onclose still act on shared globals — dispatch, scheduleReconnect.)
  socket.onopen = () => {
    reconnectAttempts = 0;
    socket.send(JSON.stringify({ id, token, isAdmin }));
    // Flush the latest heartbeat right after identifying so a freshly loaded or
    // reconnected tab registers immediately, instead of waiting for the next
    // ~30s tick — the mount heartbeat is dropped if it fires before the socket
    // is open. This assumes the server processes the identify frame before this
    // heartbeat (it sets userId from identify); frames are delivered in order,
    // but a server handler that awaits could reorder processing.
    if (lastHeartbeat) {
      socket.send(JSON.stringify({ type: WSMessageTypes.Heartbeat, ...lastHeartbeat }));
    }
  };
  socket.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      switch (data.type) {
        case WSMessageTypes.Maintenance:
          dispatch(updateMaintenance(data.maintenance) as any);
          break;
        case WSMessageTypes.Logout:
          if (!isAdmin) dispatch(logout(LogoutTypes.Maintenance) as any);
          break;
        case WSMessageTypes.PresenceSnapshot:
          dispatch(setPresenceData(data.data) as any);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
    }
  };
  socket.onclose = () => scheduleReconnect();
  socket.onerror = (e) => {
    // Log the failure event (TLS/refused/protocol) before closing — onclose
    // then drives the backoff reconnect. Without this the only signal that the
    // channel (which also carries Maintenance/Logout) is failing is swallowed.
    console.error(e);
    socket.close();
  };
};

export const connectWebSocket = (
  id: string,
  token: string,
  dispatch: Dispatch,
  isAdmin: boolean,
) => {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  identity = { id, token, dispatch, isAdmin };
  intentionalClose = false;
  if (!ws || ws.readyState === WebSocket.CLOSED) open();
};

export const sendHeartbeat = (state: 'ACTIVE' | 'IDLE', context?: string, url?: string) => {
  lastHeartbeat = { state, context, url };
  if (ws && ws.readyState === WebSocket.OPEN) {
    // The socket can transition to CLOSING between the readyState check and the
    // send (and send can throw InvalidStateError). Guard so a heartbeat sent
    // from the CourseViewer message handler can't abort the rest of that turn.
    try {
      ws.send(JSON.stringify({ type: WSMessageTypes.Heartbeat, state, context, url }));
    } catch (e) {
      console.error(e);
    }
  }
};

export const closeWebSocket = () => {
  intentionalClose = true;
  identity = undefined;
  lastHeartbeat = undefined;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  try {
    ws?.close();
  } catch (e) {
    console.error(e);
  }
};

export default connectWebSocket;
